/**
 * Generic GraphQL fetch hook for the customer portal.
 *
 * The portal does not use Apollo Client. This hook makes plain POST requests
 * to the /graphql endpoint with a Bearer token sourced from the portal auth
 * store. It returns data, loading, error, and a refetch callback.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { usePortalAuth } from './usePortalAuth.js';

interface GraphQLResponse<T> {
  data?: T;
  errors?: { message: string }[];
}

interface UseGraphQLResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useGraphQL<T>(
  query: string,
  variables?: Record<string, unknown>,
): UseGraphQLResult<T> {
  const token = usePortalAuth((state) => state.token);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A counter incremented by refetch() triggers a re-run of the effect.
  const [fetchCount, setFetchCount] = useState(0);

  // Stable JSON string of variables so the effect dependency is value-based.
  const variablesJson = JSON.stringify(variables ?? null);
  // Keep a ref to the latest token so the abort-cleanup path reads the right value.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    let cancelled = false;

    async function execute(): Promise<void> {
      setLoading(true);
      setError(null);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (tokenRef.current !== null) {
        headers['Authorization'] = `Bearer ${tokenRef.current}`;
      }

      try {
        const res = await fetch('/graphql', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            query,
            variables: JSON.parse(variablesJson) as Record<string, unknown> | null,
          }),
        });

        if (!res.ok) {
          if (!cancelled) {
            setError(`Request failed: ${String(res.status)} ${res.statusText}`);
            setLoading(false);
          }
          return;
        }

        const json = (await res.json()) as GraphQLResponse<T>;

        if (cancelled) {
          return;
        }

        if (json.errors !== undefined && json.errors.length > 0) {
          setError(json.errors.map((e) => e.message).join('; '));
          setLoading(false);
          return;
        }

        setData(json.data ?? null);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Network error');
          setLoading(false);
        }
      }
    }

    void execute();

    return (): void => {
      cancelled = true;
    };
    // fetchCount is intentionally included in deps so refetch() re-runs the effect.
  }, [query, variablesJson, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return { data, loading, error, refetch };
}
